import { PlusIcon } from '@langchain/untitled-ui-icons';

import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { KeyboardShortcut } from '@/Pages/KeyboardShortcutsSidePane';
import { IconButton } from '@/design-system';
import ShiftIcon from '@/icons/ShiftIcon.svg?react';
import { useGetGraphStudioPath } from '@/utils/useGetGraphStudioPath';

import { StateContext } from '../../control/state';
import { StudioRenderModeContext } from '../../hooks/useStudioRenderMode';

export const NewThreadButton = () => {
  const state = useContext(StateContext);
  const { renderMode } = useContext(StudioRenderModeContext);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const hostProjectId = searchParams.get('hostProjectId') ?? undefined;
  const urlThreadId = searchParams.get('threadId');

  const { generatePath } = useGetGraphStudioPath();

  const newThreadPath = generatePath({ hostProjectId, render: renderMode });
  const { setRunState, clearUpdates } = state;

  const onNewThread = useCallback(() => {
    navigate(newThreadPath);
    setRunState({ type: 'empty' });
    clearUpdates();
  }, [navigate, newThreadPath, setRunState, clearUpdates]);

  const isMac = useMemo(
    () => /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent),
    []
  );

  useEffect(() => {
    function callback(event: KeyboardEvent) {
      // Cmd/Ctrl + Shift + O
      // (Same shortcut as in ChatGPT)
      if (
        event.key.toLocaleLowerCase() === 'o' &&
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey
      ) {
        onNewThread();
      }
    }
    document.addEventListener('keydown', callback);
    return () => document.removeEventListener('keydown', callback);
  }, [onNewThread]);

  const threadId = state.threadId ?? urlThreadId;
  if (!threadId) return null;

  return (
    <IconButton
      icon={PlusIcon}
      label="New Thread"
      onClick={onNewThread}
      data-testid="new-thread-button"
      color="secondary"
      variant="outlined"
      tooltipProps={{
        title: (
          <div className="flex items-center gap-1">
            <span>New Thread</span>
            <KeyboardShortcut
              keys={[
                isMac ? '⌘' : '⌃',
                '+',
                <ShiftIcon className="h-4 w-2 fill-[var(--black)]" />,
                '+',
                'O',
              ]}
            />
          </div>
        ),
        side: 'bottom',
      }}
    />
  );
};
